import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api.js";

interface HasIdAndName {
  id: string;
  name?: string;
  displayName?: string;
}

type MetadataKind = "programs" | "indicators" | "orgUnits";

const LABELS: Record<MetadataKind, string> = {
  programs: "program",
  indicators: "indicator",
  orgUnits: "org unit"
};

function extractItems(payload: Record<string, unknown>, kind: MetadataKind): HasIdAndName[] {
  const snapshot = payload.snapshot as Record<string, unknown> | undefined;
  if (kind === "programs") {
    const programs = (snapshot?.programs as HasIdAndName[]) ?? (payload.programs as HasIdAndName[]) ?? [];
    return programs;
  }
  if (kind === "orgUnits") {
    return (snapshot?.orgUnits as HasIdAndName[]) ?? [];
  }
  return (payload.indicators as HasIdAndName[]) ?? [];
}

const cache = new Map<MetadataKind, Promise<HasIdAndName[]>>();

function loadKind(kind: MetadataKind): Promise<HasIdAndName[]> {
  if (cache.has(kind)) return cache.get(kind)!;
  const promise = (async () => {
    if (kind === "indicators") {
      try {
        const body = await api.request<{ indicators: HasIdAndName[] }>("/api/indicators");
        return body.indicators ?? [];
      } catch {
        return [];
      }
    }
    const body = await api.metadata();
    return extractItems(body, kind);
  })();
  cache.set(kind, promise);
  return promise;
}

export function clearMetadataCache(): void {
  cache.clear();
}

export interface MetadataPickerProps {
  kind: MetadataKind;
  value: string;
  onChange: (id: string) => void;
  id?: string;
  disabled?: boolean;
  allowEmpty?: boolean;
  emptyLabel?: string;
}

export default function MetadataPicker({
  kind,
  value,
  onChange,
  id,
  disabled,
  allowEmpty = true,
  emptyLabel
}: MetadataPickerProps) {
  const [items, setItems] = useState<HasIdAndName[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadKind(kind)
      .then((list) => { if (!cancelled) setItems(list); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [kind]);

  const options = useMemo(() => {
    const sorted = (items ?? []).slice().sort((a, b) =>
      (a.displayName ?? a.name ?? a.id).localeCompare(b.displayName ?? b.name ?? b.id)
    );
    return sorted;
  }, [items]);

  const label = LABELS[kind];
  const valueIsMissing = Boolean(value) && items !== null && !items.some((i) => i.id === value);

  if (items === null && !error) {
    return <select id={id} disabled><option>Loading {label}s…</option></select>;
  }
  if (error) {
    return (
      <>
        <input id={id} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} />
        <small style={{ color: "#b91c1c" }}>Metadata unavailable — enter ID manually.</small>
      </>
    );
  }
  if (options.length === 0) {
    return (
      <>
        <input id={id} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} placeholder={`${label} id`} />
        <small>No {label}s loaded yet — enter ID or run a metadata sync.</small>
      </>
    );
  }

  return (
    <>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
        {allowEmpty && <option value="">{emptyLabel ?? `— Select ${label} —`}</option>}
        {valueIsMissing && <option value={value}>Unknown: {value}</option>}
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {(o.displayName ?? o.name ?? o.id)} <span style={{ color: "#64748b" }}>({o.id})</span>
          </option>
        ))}
      </select>
      {valueIsMissing && (
        <small style={{ color: "#b45309" }}>Current value not in metadata snapshot.</small>
      )}
    </>
  );
}
